import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import Canvas from '../../components/Canvas';
import LeftSidebar from '../../components/LeftSidebar';
import RightSidebar from '../../components/RightSidebar';
import { adminApi } from '../../services/adminApi';
import './AdminTemplateBuilder.css';

function AdminTemplateBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditMode = Boolean(id);

    // Grid state
    const [grid, setGrid] = useState([]);
    const [containerWidth, setContainerWidth] = useState(600);
    const [selectedElement, setSelectedElement] = useState(null);
    const [selectedCell, setSelectedCell] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [draggedItem, setDraggedItem] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);

    // Template metadata
    const [templateName, setTemplateName] = useState('');
    const [description, setDescription] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [tags, setTags] = useState('');
    const [isActive, setIsActive] = useState(true);
    const [isFeatured, setIsFeatured] = useState(false);

    // Categories list
    const [categories, setCategories] = useState([]);

    // UI state
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    // Demo assets pour afficher des images réalistes lors de la création de templates
    const demoAssets = {
        profilePhoto: '/demo/profile.jpg',
        logo: '/demo/logo.png'
    };

    // Simple notification (pas de toast dans l'admin builder)
    const showNotification = (message, type) => {
        console.log(`[${type}] ${message}`);
    };

    // Load categories
    useEffect(() => {
        const loadCategories = async () => {
            try {
                const res = await adminApi.getCategories();
                setCategories(res.data.categories || []);
            } catch (err) {
                console.error('Erreur chargement categories:', err);
            }
        };
        loadCategories();
    }, []);

    // Load template if editing
    useEffect(() => {
        if (isEditMode) {
            loadTemplate();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadTemplate = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await adminApi.getTemplate(id);
            const template = res.data.template;

            setTemplateName(template.name || '');
            setDescription(template.description || '');
            setCategoryId(template.category_id || '');
            setTags(template.tags?.join(', ') || '');
            setIsActive(template.is_active === 1 || template.is_active === true);
            setIsFeatured(template.is_featured === 1 || template.is_featured === true);

            // Parse template_data
            const templateData = template.template_data || {};
            setGrid(templateData.grid_data || []);
            setContainerWidth(templateData.container_width || 600);
        } catch (err) {
            console.error('Erreur chargement template:', err);
            setError('Impossible de charger le template');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!templateName.trim()) {
            alert('Le nom du template est requis');
            return;
        }

        setSaving(true);
        try {
            const data = {
                name: templateName.trim(),
                description: description.trim(),
                category_id: categoryId || null,
                tags: tags.split(',').map(t => t.trim()).filter(Boolean),
                template_data: {
                    grid_data: grid,
                    container_width: containerWidth
                },
                is_active: isActive,
                is_featured: isFeatured
            };

            if (isEditMode) {
                await adminApi.updateTemplate(id, data);
            } else {
                await adminApi.createTemplate(data);
            }

            navigate('/admin/templates');
        } catch (err) {
            console.error('Erreur sauvegarde:', err);
            alert('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleClear = () => {
        if (grid.length === 0) return;
        if (window.confirm('Effacer tout le contenu du template ?')) {
            setGrid([]);
            setSelectedElement(null);
            setSelectedCell(null);
            setSelectedRow(null);
        }
    };

    // Deselect on escape
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') {
                setSelectedElement(null);
                setSelectedCell(null);
                setSelectedRow(null);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    if (loading) {
        return (
            <AdminLayout>
                <div className="admin-template-builder">
                    <div className="builder-loading">Chargement du template...</div>
                </div>
            </AdminLayout>
        );
    }

    if (error) {
        return (
            <AdminLayout>
                <div className="admin-template-builder">
                    <div className="builder-error">
                        <p>{error}</p>
                        <button onClick={() => navigate('/admin/templates')}>Retour</button>
                    </div>
                </div>
            </AdminLayout>
        );
    }

    return (
        <AdminLayout>
            <div className="admin-template-builder">
                {/* Header */}
                <div className="builder-header">
                    <div className="header-left">
                        <button className="btn-back" onClick={() => navigate('/admin/templates')}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M19 12H5M12 19l-7-7 7-7" />
                            </svg>
                            Retour
                        </button>
                        <input
                            type="text"
                            className="template-name-input"
                            value={templateName}
                            onChange={(e) => setTemplateName(e.target.value)}
                            placeholder="Nom du template *"
                        />
                    </div>
                    <div className="header-right">
                        <button
                            className={`btn-preview ${previewMode ? 'active' : ''}`}
                            onClick={() => setPreviewMode(!previewMode)}
                        >
                            {previewMode ? 'Editer' : 'Apercu'}
                        </button>
                        <button className="btn-clear" onClick={handleClear} disabled={grid.length === 0}>
                            Effacer
                        </button>
                        <button className="btn-save" onClick={handleSave} disabled={saving}>
                            {saving ? 'Sauvegarde...' : 'Sauvegarder'}
                        </button>
                    </div>
                </div>

                {/* Metadata */}
                <div className="builder-metadata">
                    <div className="metadata-field">
                        <label>Description</label>
                        <input
                            type="text"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Description du template"
                        />
                    </div>
                    <div className="metadata-field">
                        <label>Categorie</label>
                        <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
                            <option value="">Sans categorie</option>
                            {categories.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>
                    <div className="metadata-field">
                        <label>Tags</label>
                        <input
                            type="text"
                            value={tags}
                            onChange={(e) => setTags(e.target.value)}
                            placeholder="tag1, tag2, tag3"
                        />
                    </div>
                    <div className="metadata-field checkbox-field">
                        <label>
                            <input
                                type="checkbox"
                                checked={isActive}
                                onChange={(e) => setIsActive(e.target.checked)}
                            />
                            Actif
                        </label>
                    </div>
                    <div className="metadata-field checkbox-field">
                        <label>
                            <input
                                type="checkbox"
                                checked={isFeatured}
                                onChange={(e) => setIsFeatured(e.target.checked)}
                            />
                            Mis en avant
                        </label>
                    </div>
                    <div className="metadata-field width-field">
                        <label>Largeur</label>
                        <input
                            type="number"
                            value={containerWidth}
                            onChange={(e) => setContainerWidth(parseInt(e.target.value) || 600)}
                            min="400"
                            max="800"
                        />
                        <span>px</span>
                    </div>
                </div>

                {/* Builder */}
                <div className="builder-content">
                    <LeftSidebar
                        onDragStart={setDraggedItem}
                        previewMode={previewMode}
                    />

                    <div className="builder-canvas-container">
                        <Canvas
                            grid={grid}
                            setGrid={setGrid}
                            draggedItem={draggedItem}
                            setDraggedItem={setDraggedItem}
                            selectedElement={selectedElement}
                            setSelectedElement={setSelectedElement}
                            selectedCell={selectedCell}
                            setSelectedCell={setSelectedCell}
                            selectedRow={selectedRow}
                            setSelectedRow={setSelectedRow}
                            containerWidth={containerWidth}
                            setContainerWidth={setContainerWidth}
                            previewMode={previewMode}
                            demoAssets={demoAssets}
                            showNotification={showNotification}
                        />
                    </div>

                    <RightSidebar
                        grid={grid}
                        setGrid={setGrid}
                        selectedElement={selectedElement}
                        setSelectedElement={setSelectedElement}
                        selectedCell={selectedCell}
                        setSelectedCell={setSelectedCell}
                        containerWidth={containerWidth}
                        previewMode={previewMode}
                    />
                </div>
            </div>
        </AdminLayout>
    );
}

export default AdminTemplateBuilder;
